import { useCallback, useMemo, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useFormsConfig } from "./Context";
import { useHasPermission } from "../../../common/Hooks";
import Utils from "../../../utils/utils";
import PubSub from "../../../services/PubSub";
import EditDataList from "./modals/EditDataList";
import SearchBar from "../../controls/SearchBar";
import ListStatus from "./controls/ListStatus";

const DataListsEditor = () => {
    const { id: deploymentId } = useParams();
    const { dataLists } = useFormsConfig();
    const [translate] = useTranslation();
    const ref = useRef();

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });

    const [showAddDataList, setShowAddDataList] = useState(false);
    const [showEditDataList, setShowEditDataList] = useState(false);

    const [searchPrompt, setSearchPrompt] = useState('');

    const renderListItemAtIndex = useCallback((options, index) => {
        if (options && options.length > 0 && options[index]) {
            return <li className="list-group-item border-0 p-0 small">{translate(options[index].displayValue)}</li>;
        }
        else {
            return null;
        }
    }, [translate]);

    const renderListItems = useCallback((dataList) => {
        if (dataList && dataList.options && dataList.options.length > 0) {
            return <div className="mt-2 mb-auto">
                <ol className="list-group list-group-numbered list-group-flush">
                    {renderListItemAtIndex(dataList.options, 0)}
                    {renderListItemAtIndex(dataList.options, 1)}
                    {renderListItemAtIndex(dataList.options, 2)}
                </ol>
                {dataList.options.length > 3 ? <label className="small">{translate("OTM_EXTRA_OPTIONS_INDICATOR", { length: dataList.options.length - 3 })}</label> : null}
            </div>;
        }
        else {
            return null;
        }
    }, [renderListItemAtIndex, translate]);

    const filterDataLists = useCallback((dataList) => {
        if (dataList.name !== undefined && dataList.name.toLowerCase().includes(searchPrompt.toLowerCase())) {
            return true;
        }

        if (dataList.options && dataList.options.length > 0) {
            return dataList.options.filter((option) => {
                return (option.value && option.value.toLowerCase().includes(searchPrompt.toLowerCase())) ||
                    (option.displayValue && option.displayValue.toLowerCase().includes(searchPrompt.toLowerCase()))
            }).length > 0;
        }
    }, [searchPrompt]);

    const dataListItems = useMemo(() => dataLists ? dataLists.filter((dataList) => filterDataLists(dataList)) : [], [dataLists, filterDataLists]);

    const renderDataLists = useMemo(() => {
        if (dataListItems && dataListItems.length > 0) {
            return (
                <div className="flex-lg-grow-1 overflow-x-hidden overflow-y-lg-auto padded-scrollbar">
                    <div className="row mt-n3">
                        {dataListItems.map((dataList, index) => (
                            <div
                                className="col-12 col-md-6 col-lg-4 mt-3"
                                key={index}
                            >
                                <div className="h-100 card border">
                                    <div className="card-body d-flex flex-column">
                                        <label className="d-block h6">{dataList.name}</label>
                                        {renderListItems(dataList)}
                                        {permissions.update ? <div className="row gx-2 justify-content-end">
                                            <div className="col-auto mt-2">
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => { setShowEditDataList(dataList._id); }}
                                                >{translate("OTM_EDIT")}</Button>
                                            </div>
                                        </div> : null}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )
        }
        else {
            return <ListStatus loading={false} error={false} empty={true} ></ListStatus>;
        }
    }, [dataListItems, permissions, renderListItems, translate]);

    return <>
        {showAddDataList &&
            <EditDataList
                show={showAddDataList}
                onAdd={() => { PubSub.publish('refresh-data-lists'); setShowAddDataList(false) }}
                onClose={() => { setShowAddDataList(false) }} />
        }
        {showEditDataList &&
            <EditDataList
                show={showEditDataList}
                dataList={dataLists.find(o => o._id === showEditDataList)}
                onUpdate={() => { PubSub.publish('refresh-data-lists'); setShowEditDataList(false) }}
                onClose={() => { setShowEditDataList(false) }} />
        }
        <div className="d-flex align-items-center mb-3">
            <SearchBar ref={ref} size="sm" initialValue={searchPrompt} placeholder={translate("OTM_SEARCH")} onChange={(value) => setSearchPrompt(value)} />
            {permissions.update ? <Button size="sm" className="ms-3" onClick={() => { setShowAddDataList(true) }}>{translate("OTM_ADD")}</Button> : null}
        </div>
        {renderDataLists}
    </>
}

export default DataListsEditor;